import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface WishlistContextType {
  wishlistCount: number;
  wishlistIds: Set<string>;
  addToWishlist: (productId: string) => Promise<boolean>;
  removeFromWishlist: (productId: string) => Promise<boolean>;
  isInWishlist: (productId: string) => boolean;
  refreshWishlist: () => Promise<void>;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export const WishlistProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [wishlistIds, setWishlistIds] = useState<Set<string>>(new Set());
  const [wishlistMap, setWishlistMap] = useState<Map<string, string>>(new Map()); // productId -> wishlistId

  const refreshWishlist = useCallback(async () => {
    if (!user) {
      setWishlistIds(new Set());
      setWishlistMap(new Map());
      return;
    }

    const { data, error } = await supabase
      .from('wishlist')
      .select('id, product_id')
      .eq('user_id', user.id);

    if (!error && data) {
      const ids = new Set(data.map(item => item.product_id));
      const map = new Map(data.map(item => [item.product_id, item.id]));
      setWishlistIds(ids);
      setWishlistMap(map);
    }
  }, [user]);

  useEffect(() => {
    refreshWishlist();
  }, [refreshWishlist]);

  // Subscribe to realtime changes
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('wishlist-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'wishlist',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          refreshWishlist();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, refreshWishlist]);

  const addToWishlist = async (productId: string): Promise<boolean> => {
    if (!user) {
      toast.error('Please log in to add items to your wishlist');
      return false;
    }

    const { error } = await supabase
      .from('wishlist')
      .insert({ user_id: user.id, product_id: productId });

    if (error) {
      if (error.code === '23505') {
        // Already in wishlist
        return true;
      }
      toast.error('Failed to add to wishlist');
      return false;
    }

    await refreshWishlist();
    return true;
  };

  const removeFromWishlist = async (productId: string): Promise<boolean> => {
    if (!user) return false;

    const wishlistId = wishlistMap.get(productId);
    if (!wishlistId) return false;

    const { error } = await supabase
      .from('wishlist')
      .delete()
      .eq('id', wishlistId);

    if (error) {
      toast.error('Failed to remove from wishlist');
      return false;
    }

    await refreshWishlist();
    return true;
  };

  const isInWishlist = (productId: string): boolean => {
    return wishlistIds.has(productId);
  };

  return (
    <WishlistContext.Provider
      value={{
        wishlistCount: wishlistIds.size,
        wishlistIds,
        addToWishlist,
        removeFromWishlist,
        isInWishlist,
        refreshWishlist,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) throw new Error('useWishlist must be used within WishlistProvider');
  return context;
};
